import {
	BarChart3,
	CirclePause,
	CirclePlay,
	ClipboardList,
	Link2,
	Pencil,
	Plus,
	Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCampaignsAdmin,
	useDeleteCampaign,
	useUpdateCampaignStatus,
} from '@/api/queries/campaigns-queries'
import { AdminCardPage, ConfirmDialog, SearchInput } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import type { CampaignAdmin, CampaignAvailability } from '@/types/campaign'
import { getApiErrorMessage } from '@/utils/api-error'
import { formatLocalDate } from '@/utils/campaign-dates'

const AVAILABILITY_STYLE: Record<CampaignAvailability, string> = {
	DRAFT: 'bg-kaefer-medium-grey/15 text-kaefer-dark-grey',
	SCHEDULED: 'bg-kaefer-honey-yellow/25 text-kaefer-dark-grey',
	OPEN: 'bg-kaefer-forest-green/15 text-kaefer-forest-green',
	ENDED: 'bg-kaefer-red/10 text-kaefer-red',
}

/** Lista de campanhas para ADMIN e SSMA_MANAGER: criar, publicar, copiar o link e abrir os resultados. */
export const CampaignsAdminPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data, isLoading, isError } = useCampaignsAdmin()
	const statusMutation = useUpdateCampaignStatus()
	const deleteMutation = useDeleteCampaign()

	const [search, setSearch] = useState('')
	const [deleting, setDeleting] = useState<CampaignAdmin | null>(null)

	const campaigns = (data?.items ?? []).filter((campaign) =>
		campaign.title.toLowerCase().includes(search.toLowerCase()),
	)

	const copyLink = async (campaign: CampaignAdmin) => {
		const link = `${window.location.origin}${ROUTES.DASHBOARD.CAMPAIGN_DETAIL.replace(':id', campaign.id)}`
		try {
			await navigator.clipboard.writeText(link)
			toast.success(t('campaigns.admin.linkCopied'))
		} catch {
			toast.error(t('campaigns.admin.linkCopyError'))
		}
	}

	const togglePublished = async (campaign: CampaignAdmin) => {
		const publishing = campaign.status !== 'PUBLISHED'
		try {
			await statusMutation.mutateAsync({
				id: campaign.id,
				status: publishing ? 'PUBLISHED' : 'CLOSED',
			})
			toast.success(
				t(publishing ? 'campaigns.admin.published' : 'campaigns.admin.closed'),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const confirmDelete = async () => {
		if (!deleting) return
		try {
			await deleteMutation.mutateAsync(deleting.id)
			toast.success(t('campaigns.admin.deleted'))
			setDeleting(null)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<AdminCardPage
			icon={<ClipboardList className="size-5" />}
			title={t('campaigns.title')}
			subtitle={t('campaigns.admin.subtitle')}
			onBack={() => navigate(adminBackRoute(ROUTES.ADMINISTRATION.CAMPAIGNS))}
			actions={
				<Button
					type="button"
					className="w-auto gap-2"
					onClick={() => navigate(ROUTES.ADMINISTRATION.CAMPAIGNS_FORM)}
				>
					<Plus className="size-4" />
					{t('campaigns.admin.create')}
				</Button>
			}
			footerText={t('campaigns.admin.count', { count: campaigns.length })}
		>
			<SearchInput
				value={search}
				onChange={setSearch}
				placeholder={t('common.search')}
			/>

			{isLoading ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			) : isError ? (
				<p className="py-16 text-center text-destructive text-sm">
					{t('campaigns.admin.loadError')}
				</p>
			) : campaigns.length === 0 ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('campaigns.admin.empty')}
				</p>
			) : (
				<div className="flex flex-col gap-3">
					{campaigns.map((campaign) => (
						<div
							key={campaign.id}
							className="flex flex-col gap-3 rounded-lg border border-kaefer-light-silver bg-white p-4 lg:flex-row lg:items-center lg:justify-between"
						>
							<div className="min-w-0">
								<div className="flex flex-wrap items-center gap-2">
									<h3 className="truncate font-semibold text-kaefer-dark-grey">
										{campaign.title}
									</h3>
									<span
										className={cn(
											'rounded-full px-2 py-0.5 text-xs font-semibold',
											AVAILABILITY_STYLE[campaign.availability],
										)}
									>
										{t(`campaigns.availability.${campaign.availability}`)}
									</span>
								</div>
								<p className="mt-1 text-xs text-muted-foreground">
									{t('campaigns.admin.summary', {
										responses: campaign.responseCount,
										questions: campaign.questions.length,
									})}
									{' · '}
									{campaign.isGlobal
										? t('campaigns.admin.scopeGlobal')
										: t('campaigns.admin.scopeBranches', {
												count: campaign.branchIds.length,
											})}
									{campaign.startsAt || campaign.endsAt
										? ` · ${campaign.startsAt ? formatLocalDate(campaign.startsAt) : '...'} – ${campaign.endsAt ? formatLocalDate(campaign.endsAt) : '...'}`
										: ''}
								</p>
							</div>

							<div className="flex flex-wrap items-center gap-1">
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-9 w-auto px-2"
									title={t('campaigns.admin.copyLink')}
									onClick={() => copyLink(campaign)}
								>
									<Link2 className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-9 w-auto px-2"
									title={t(
										campaign.status === 'PUBLISHED'
											? 'campaigns.admin.close'
											: 'campaigns.admin.publish',
									)}
									disabled={statusMutation.isPending}
									onClick={() => togglePublished(campaign)}
								>
									{campaign.status === 'PUBLISHED' ? (
										<CirclePause className="size-4" />
									) : (
										<CirclePlay className="size-4" />
									)}
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-9 w-auto px-2"
									title={t('campaigns.admin.results')}
									onClick={() =>
										navigate(
											ROUTES.ADMINISTRATION.CAMPAIGN_RESULTS.replace(
												':id',
												campaign.id,
											),
										)
									}
								>
									<BarChart3 className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-9 w-auto px-2"
									title={t('campaigns.admin.edit')}
									onClick={() =>
										navigate(
											`${ROUTES.ADMINISTRATION.CAMPAIGNS_FORM}?id=${campaign.id}`,
										)
									}
								>
									<Pencil className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-9 w-auto px-2 text-destructive"
									title={t('campaigns.admin.delete')}
									onClick={() => setDeleting(campaign)}
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						</div>
					))}
				</div>
			)}

			<ConfirmDialog
				open={!!deleting}
				onOpenChange={(open) => !open && setDeleting(null)}
				title={t('campaigns.admin.deleteTitle')}
				onConfirm={confirmDelete}
				isPending={deleteMutation.isPending}
				confirmLabel={t('campaigns.admin.delete')}
				cancelLabel={t('common.cancel')}
				destructive
			>
				{t('campaigns.admin.deleteMessage', { title: deleting?.title })}
			</ConfirmDialog>
		</AdminCardPage>
	)
}
